import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ColumnDef } from "@tanstack/react-table";
import CollapseArrowIcon from "../../../../../assets/svg/collapse-arrow-icon";
import InfoBtnIcon from "../../../../../assets/svg/info-btn-icon";
import PinIcon from "../../../../../assets/svg/pin-icon";
import Table from "../../../../../components/Table/Table";
import Pagination from "../../../../../components/Table/Pagination";
import { useGetLeadsMutation } from "../../../../../redux/services/crmApi";
import { Lead, Stage, Pipeline } from "../../../utils/types";
import {
  getLeadTableColumns,
  getFilteredLeadTableColumns,
  type LeadTableSelectionState,
  type LeadTableCallbacks,
  type LeadTableFilterCallbacks,
  type LeadTableFilterState,
} from "../utlis/leadTableColumns";
import type { RootState } from "../../../../../redux/store";
import { toggleCollapsedStage } from "../../../../../redux/slices/crmLeadListSlice";
import { usePersistedPagination } from "../../../../../hooks/usePersistedPagination";
const NON_PINNABLE_COLUMNS = new Set(["selection", "arrow"]);
const DEFAULT_ROWS_PER_PAGE = 10;

interface LeadTableListingProps {
  stage: Stage;
  viewId?: string;
  crmViewId?: string;
  globalCrmFilters: Record<string, unknown>;
  tableFilters: Record<string, unknown>;
  stageColorMap: Record<string, string>;
  stageNameMap: Record<string, string>;
  visibleColumnKeys?: string[];
  pinnedColumns?: string[];
  onPinColumn?: (columnId: string) => void;
  onLeadUpdate?: (lead: Lead, stageId: string) => void;
  contactOwner: string[];
  neighbourhood: string | null;
  onContactOwnerApply: (ids: string[]) => void;
  onContactOwnerReset: () => void;
  onNeighbourhoodSelect: (id: string | null) => void;
  createdDate: string | null;
  lastActivity: string | null;
  onCreatedDateSelect: (preset: string | null) => void;
  onLastActivitySelect: (preset: string | null) => void;
  onInfoClick?: () => void;
}

export const LeadTableListing = ({
  stage,
  viewId,
  crmViewId,
  globalCrmFilters,
  tableFilters,
  stageColorMap,
  stageNameMap,
  visibleColumnKeys,
  pinnedColumns = [],
  onPinColumn,
  onLeadUpdate,
  contactOwner,
  neighbourhood,
  onContactOwnerApply,
  onContactOwnerReset,
  onNeighbourhoodSelect,
  createdDate,
  lastActivity,
  onCreatedDateSelect,
  onLastActivitySelect,
  onInfoClick,
}: LeadTableListingProps) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const collapsedStageIds = useSelector(
    (state: RootState) => state.crmLeadList.collapsedStageIds
  );
  const isCollapsed = collapsedStageIds?.includes(stage?.stage);

  const handleToggleCollapse = useCallback(() => {
    dispatch(toggleCollapsedStage(stage?.stage));
  }, [dispatch, stage?.stage]);

  const [fetchLeads] = useGetLeadsMutation();

  const {
    page: currentPage,
    setPage: setCurrentPage,
    limit: rowsPerPage,
    setLimit: setRowsPerPage,
  } = usePersistedPagination(`crm-lead:${stage?.stage}`, {
    defaultLimit: DEFAULT_ROWS_PER_PAGE,
    resetOn: [contactOwner, neighbourhood, createdDate, lastActivity],
  });
  const [leads, setLeads] = useState<Lead[]>(stage?.leads ?? []);
  const [totalCount, setTotalCount] = useState(stage?.count ?? 0);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(
    new Set()
  );
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / rowsPerPage)),
    [totalCount, rowsPerPage]
  );

  const isPaginated = currentPage > 1 || rowsPerPage !== DEFAULT_ROWS_PER_PAGE;

  useEffect(() => {
    if (!isPaginated) {
      setLeads(stage?.leads ?? []);
      setTotalCount(stage?.count ?? 0);
    }
  }, [isPaginated, stage?.leads, stage?.count]);

  useEffect(() => {
    if (!isPaginated) return;

    const fetchPaginatedLeads = async () => {
      setIsFetching(true);
      try {
        const skip = (currentPage - 1) * rowsPerPage;
        const filtersToSend = crmViewId
          ? Object.fromEntries(
              Object.entries(globalCrmFilters).filter(
                ([k]) => k !== "start_date" && k !== "end_date"
              )
            )
          : globalCrmFilters;
        const response = await fetchLeads({
          limit: rowsPerPage,
          skip,
          stage: stage?.stage ? [stage.stage] : [],
          ...filtersToSend,
          ...tableFilters,
          ...(crmViewId
            ? { view_id: crmViewId }
            : viewId
              ? { pipeline_id: viewId }
              : {}),
        });
        if ("data" in response) {
          const pipelinesData = response.data as Pipeline[];
          const matchedStage = pipelinesData
            .flatMap((p) => p.stages)
            .find((s) => s.stage === stage?.stage);
          if (matchedStage) {
            setLeads(matchedStage.leads);
            setTotalCount(matchedStage.count);
          }
        }
      } catch (error) {
        console.error("Error fetching paginated leads:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchPaginatedLeads();
  }, [
    isPaginated,
    currentPage,
    rowsPerPage,
    fetchLeads,
    stage?.stage,
    globalCrmFilters,
    tableFilters,
    crmViewId,
    viewId,
  ]);

  const handleRowClick = useCallback(
    (lead: Lead) => {
      navigate(`/dashboard/crm/${lead?.lead_id}`);
    },
    [navigate]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1) return;
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const handleRowsPerPageChange = useCallback(
    (size: number) => {
      setRowsPerPage(size);
      setCurrentPage(1);
    },
    [setCurrentPage, setRowsPerPage]
  );

  const handleToggleSelect = useCallback((leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  }, []);

  const allSelected = leads.length > 0 && selectedLeadIds.size === leads.length;

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(leads.map((l) => l.lead_id)));
    }
  }, [allSelected, leads]);

  const selection: LeadTableSelectionState = useMemo(
    () => ({
      selectedIds: selectedLeadIds,
      allSelected,
      onToggle: handleToggleSelect,
      onToggleAll: handleToggleAll,
    }),
    [selectedLeadIds, allSelected, handleToggleSelect, handleToggleAll]
  );

  const callbacks: LeadTableCallbacks = useMemo(
    () => ({ onLeadUpdate, stageId: stage?.stage }),
    [onLeadUpdate, stage?.stage]
  );

  const handleToggleFilter = useCallback((col: string) => {
    setActiveFilterColumn((prev) => (prev === col ? null : col));
  }, []);

  const filterState: LeadTableFilterState = useMemo(
    () => ({
      contactOwner,
      neighbourhood,
      createdDate,
      lastActivity,
    }),
    [contactOwner, neighbourhood, createdDate, lastActivity]
  );

  const filterCallbacks: LeadTableFilterCallbacks = useMemo(
    () => ({
      activeFilter: activeFilterColumn,
      onToggleFilter: handleToggleFilter,
      filterState,
      onContactOwnerApply,
      onContactOwnerReset,
      onNeighbourhoodSelect,
      onCreatedDateSelect,
      onLastActivitySelect,
    }),
    [
      activeFilterColumn,
      handleToggleFilter,
      filterState,
      onContactOwnerApply,
      onContactOwnerReset,
      onNeighbourhoodSelect,
      onCreatedDateSelect,
      onLastActivitySelect,
    ]
  );

  useEffect(() => {
    if (!activeFilterColumn) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !target?.closest?.("[data-table-filter-dropdown]") &&
        !target?.closest?.("[data-table-filter-trigger]")
      ) {
        setActiveFilterColumn(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeFilterColumn]);

  const baseColumns = useMemo<ColumnDef<Lead>[]>(
    () =>
      visibleColumnKeys
        ? getFilteredLeadTableColumns(
            stageColorMap,
            stageNameMap,
            visibleColumnKeys,
            selection,
            callbacks,
            filterCallbacks
          )
        : getLeadTableColumns(
            stageColorMap,
            stageNameMap,
            selection,
            callbacks,
            filterCallbacks
          ),
    [
      stageColorMap,
      stageNameMap,
      visibleColumnKeys,
      selection,
      callbacks,
      filterCallbacks,
    ]
  );

  const columns = useMemo<ColumnDef<Lead>[]>(() => {
    if (!onPinColumn) return baseColumns;

    return baseColumns.map((col) => {
      const columnId =
        (col as { accessorKey?: string }).accessorKey ?? col.id ?? "";

      if (NON_PINNABLE_COLUMNS.has(columnId)) return col;

      const isPinned = pinnedColumns.includes(columnId);
      const originalHeader = col.header;

      return {
        ...col,
        header: (ctx: unknown) => {
          const headerContent =
            typeof originalHeader === "function"
              ? (originalHeader as (ctx: unknown) => React.ReactNode)(ctx)
              : originalHeader;

          return (
            <div className="flex items-center justify-between group">
              <div className="flex-1 min-w-0">{headerContent}</div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPinColumn(columnId);
                }}
                className={`shrink-0 mr-2 transition-opacity duration-200 ${
                  isPinned
                    ? "opacity-100 text-verba-blue dark:text-verba-blue-dk"
                    : "opacity-0 group-hover:opacity-100 text-gray-200 dark:text-gray-200-dk"
                }`}
                title={isPinned ? "Unpin column" : "Pin column"}
              >
                <PinIcon className="w-[18px] h-[18px]" />
              </button>
            </div>
          );
        },
      } as ColumnDef<Lead>;
    });
  }, [baseColumns, pinnedColumns, onPinColumn]);

  return (
    <div>
      <div
        className={`flex items-center gap-3 cursor-pointer select-none w-max ${
          !isCollapsed ? "mb-3" : ""
        }`}
        onClick={handleToggleCollapse}
      >
        <CollapseArrowIcon
          className={`w-4 h-4 shrink-0 text-verba-indigo dark:text-verba-indigo-dk transition-transform duration-200 ${
            isCollapsed ? "-rotate-90" : ""
          }`}
        />
        <button
          className="flex items-center gap-2.5 px-3 py-1 rounded-lg"
          style={{ backgroundColor: stage?.base_color }}
        >
          <span className="text-md font-semibold text-white tracking-wide uppercase">
            {stage?.name}
          </span>
          <span className="text-sm font-medium text-white">{totalCount}</span>
        </button>
        {onInfoClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onInfoClick();
            }}
          >
            <InfoBtnIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk cursor-pointer" />
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          <Table
            COLUMNS={columns}
            COLUMNS_DATA={leads}
            pinnedColumns={pinnedColumns}
            ariaLabel={`${stage?.name?.toUpperCase()} leads table`}
            cellClassName="!p-0 !h-[44px] cursor-pointer"
            isLoading={isFetching}
            rowCount={7}
            onRowClick={handleRowClick}
          />
          {totalPages > 0 && leads?.length > 0 && (
            <div className="mt-3">
              <Pagination
                rowsPerPage={rowsPerPage}
                handleRowsPerPageChange={handleRowsPerPageChange}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
                rowsPerPageText="rows"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
